import dayjs from 'dayjs'

import {
  DataMigrationPhase,
  MigrationProvider,
} from 'src/graphql/types/dataMigrations'

/** Poll the migrations list while any row is still advancing on its own. */
export const MIGRATIONS_POLL_INTERVAL_MS = 15_000

/** A heartbeat older than this while in an active phase reads as stalled. */
export const STALL_THRESHOLD_MINUTES = 30

export const PROVIDER_LABELS: Record<MigrationProvider, string> = {
  [MigrationProvider.TENSTREET]: 'TenStreet',
  [MigrationProvider.DRIVER_REACH]: 'DriverReach',
  [MigrationProvider.CSV]: 'CSV',
  [MigrationProvider.COMPANY]: 'Custom',
  [MigrationProvider.OTHER]: 'Other',
}

export const formatProviderLabel = (provider: string): string =>
  PROVIDER_LABELS[provider as MigrationProvider] ?? provider

/** -ING phases: a machine is actively running and the row advances on its own. */
const ACTIVE_PHASES = new Set<string>([
  DataMigrationPhase.DOWNLOADING,
  DataMigrationPhase.EXTRACTING,
  DataMigrationPhase.UPLOADING,
  DataMigrationPhase.TRANSFORMING,
  DataMigrationPhase.STAGING_APPLICANTS,
  DataMigrationPhase.MIGRATING,
])

/** Parked phases: waiting on a defined trigger, no stall alarm. */
const PARKED_PHASES = new Set<string>([
  DataMigrationPhase.INITIATED,
  DataMigrationPhase.UPLOADED,
  DataMigrationPhase.READY_FOR_REVIEW,
  DataMigrationPhase.READY_TO_MIGRATE,
])

const TERMINAL_PHASES = new Set<string>([
  DataMigrationPhase.COMPLETED,
  DataMigrationPhase.FAILED,
  DataMigrationPhase.CANCELLED,
])

export const isActivePhase = (phase: string): boolean =>
  ACTIVE_PHASES.has(phase)

export const isParkedPhase = (phase: string): boolean =>
  PARKED_PHASES.has(phase)

export const isTerminalPhase = (phase: string): boolean =>
  TERMINAL_PHASES.has(phase)

/** Phases that warrant polling: still-running machines plus a fresh INITIATED
 * row that a machine is about to pick up. UPLOADED polls too even though it is
 * a parked phase — for every provider but OTHER/COMPANY the backend advances it
 * to TRANSFORMING right away, so without polling the row would look frozen. */
export const isPollingPhase = (phase: string): boolean =>
  isActivePhase(phase) ||
  phase === DataMigrationPhase.INITIATED ||
  phase === DataMigrationPhase.UPLOADED

/** Mantine Badge color per the phase display convention:
 * active = blue, parked = gray, COMPLETED = green, FAILED = red, CANCELLED = gray. */
export const getPhaseColor = (phase: string): string => {
  if (phase === DataMigrationPhase.COMPLETED) return 'green'
  if (phase === DataMigrationPhase.FAILED) return 'red'
  if (isActivePhase(phase)) return 'blue'
  return 'gray'
}

type MigrationHeartbeat = {
  phase: string
  lastHeartbeatAt?: string | null
  updatedAt: string
}

/**
 * Phases with a real liveness signal: the Fargate poll cycle stamps
 * lastHeartbeatAt every 5 minutes. Nothing writes a heartbeat during
 * TRANSFORMING / STAGING_APPLICANTS / MIGRATING, so a silent row there means
 * "we have no signal", not "it is stuck" — flagging those would fire on every
 * healthy multi-hour transform and train people to ignore the warning. Those
 * phases are covered instead by the functions' onFailure handlers, which mark
 * a genuinely dead run FAILED.
 */
const HEARTBEAT_PHASES = new Set<string>([
  DataMigrationPhase.DOWNLOADING,
  DataMigrationPhase.EXTRACTING,
  DataMigrationPhase.UPLOADING,
])

/** Heartbeat-reporting row that has gone quiet for too long. */
export const isStalled = (
  migration: MigrationHeartbeat,
  now: dayjs.Dayjs = dayjs()
): boolean => {
  if (!HEARTBEAT_PHASES.has(migration.phase)) return false
  const lastSeen = migration.lastHeartbeatAt ?? migration.updatedAt
  if (!lastSeen) return false
  return now.diff(dayjs(lastSeen), 'minute') >= STALL_THRESHOLD_MINUTES
}

type MigrationProgress = {
  phase: string
  progress?: { percent?: number | null } | null
  transformedFileCount?: number | null
  rawFileCount?: number | null
}

/**
 * Progress column text. For TRANSFORMING, file counts only — the progress JSON
 * is left over from the Fargate upload, so falling back to it would show a
 * stale "95%" while transformation has barely started. Percent from the
 * progress JSON applies to the Fargate phases it was written for; em-dash when
 * there is nothing truthful to show.
 */
export const formatProgress = (migration: MigrationProgress): string => {
  if (migration.phase === DataMigrationPhase.TRANSFORMING) {
    if (
      migration.transformedFileCount != null &&
      migration.rawFileCount != null &&
      migration.rawFileCount > 0
    ) {
      return `${migration.transformedFileCount}/${migration.rawFileCount} files`
    }
    return '—'
  }

  const percent = migration.progress?.percent
  if (isActivePhase(migration.phase) && typeof percent === 'number') {
    return `${Math.round(percent)}%`
  }

  return '—'
}

/** Hostname of the source URL for the detail drawer (never the full URL). */
export const formatSourceHost = (sourceUrl?: string | null): string | null => {
  if (!sourceUrl) return null
  try {
    return new URL(sourceUrl).host
  } catch {
    return sourceUrl
  }
}

/** Compact human duration between two timestamps for the phase timeline. */
export const formatDuration = (
  from: string | Date,
  to: string | Date
): string => {
  const seconds = Math.max(0, dayjs(to).diff(dayjs(from), 'second'))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ${minutes % 60}m`
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}
